import Vue from 'vue'
import readableLocalDateTimeOptions from '../../readableLocalDateTimeOptions'

const convertUTCToReadableLocalDateWeekdayString = (timestamp) => {
  return (
    new Intl.DateTimeFormat(
      readableLocalDateTimeOptions.locale,
      readableLocalDateTimeOptions.dateWeekdayString
    ).format(timestamp)
  )
}

const patients = {
  namespaced: true,
  state: {
    patients: {},
    injections: {},
    appointments: {},
    isStoreModuleLoaded: null
  },
  getters: {
    getPatientByUsername: state => username => {
      return state.patients[username]
    },
    getEventByGroupAndId: state => (eventGroup, eventId) => {
      return state[eventGroup][eventId]
    },
    getInjectionsByPatient: (state, getters) => username => {
      return getters.injectionsByPatient[username]
    },
    getAppointmentsByPatient: (state, getters) => username => {
      return getters.appointmentsByPatient[username]
    },
    usernames (state) {
      return Object.keys(state.patients)
    },
    emails (state) {
      return (
        Object.values(state.patients)
          .map(patient => patient.email)
      )
    },
    injectionsArray (state) {
      return (
        Object.values(state.injections)
          .sort((eventA, eventB) => eventA.date_time - eventB.date_time)
      )
    },
    appointmentsArray (state) {
      return (
        Object.values(state.appointments)
          .sort((eventA, eventB) => eventA.date_time - eventB.date_time)
      )
    },
    injectionsByPatient (state, getters) {
      return (
        getters.usernames
          .reduce(
            (sortedInjections, username) => {
              sortedInjections[username] = (
                getters.injectionsArray
                  .filter(event => event.username === username)
                  .map(event => event.event_id)
              )
              return sortedInjections
            },
            {}
          )
      )
    },
    appointmentsByPatient (state, getters) {
      return (
        getters.usernames
          .reduce(
            (sortedAppointments, username) => {
              sortedAppointments[username] = (
                getters.appointmentsArray
                  .filter(event => event.username === username)
                  .map(event => event.event_id)
              )
              return sortedAppointments
            },
            {}
          )
      )
    },
    injectionDays (state, getters) {
      return (
        Array.from(
          new Set(
            getters.injectionsArray
              .map(event => convertUTCToReadableLocalDateWeekdayString(event.date_time))
          )
        )
      )
    },
    injectionCalendar (state, getters) {
      return (
        getters.injectionDays.reduce(
          (days, day) => {
            days[day] = []

            return days
          },
          {}
        )
      )
    },
    appointmentDays (state, getters) {
      return (
        Array.from(
          new Set(
            getters.appointmentsArray
              .map(event => convertUTCToReadableLocalDateWeekdayString(event.date_time))
          )
        )
      )
    },
    appointmentCalendar (state, getters) {
      return (
        getters.appointmentDays.reduce(
          (days, day) => {
            days[day] = []

            return days
          },
          {}
        )
      )
    },
    injectionsByDay (state, getters) {
      let calendar = { ...getters.injectionCalendar }
      getters.injectionsArray.forEach(
        injection => {
          let date = convertUTCToReadableLocalDateWeekdayString(injection.date_time)

          // if (!calendar.hasOwnProperty(date)) {
          //   calendar[date] = []
          // }

          calendar[date].push(injection)
        }
      )
      return calendar
    },
    appointmentsByDay (state, getters) {
      let calendar = { ...getters.appointmentCalendar }
      getters.appointmentsArray.forEach(
        appointment => {
          let date = convertUTCToReadableLocalDateWeekdayString(appointment.date_time)

          // if (!calendar.hasOwnProperty(date)) {
          //   calendar[date] = []
          // }

          calendar[date].push(appointment)
        }
      )
      return calendar
    }
  },
  mutations: {
    setOnePatient (state, payload) {
      // REF: https://vuex.vuejs.org/guide/mutations.html#mutations-follow-vue-s-reactivity-rules
      state.patients = {
        ...state.patients,
        [payload.key]: Object.assign(state.patients[payload.key] || {}, payload.value)
      }
    },
    removeOnePatient (state, username) {
      Vue.delete(
        state.patients,
        username
      )
    },
    setOneEvent (state, payload) {
      Vue.set(
        state[payload.eventGroup],
        String(payload.key),
        Object.assign(
          state[payload.eventGroup][payload.key] || {},
          payload.value
        )
      )
    },
    removeOneEvent (state, payload) {
      Vue.delete(
        state[payload.eventGroup],
        payload.eventId
      )
    },
    removeManyInjections (state, eventIds) {
      eventIds.forEach(eventId => {
        Vue.delete(
          state.injections,
          eventId
        )
      })
    },
    addManyInjections (state, events) {
      events.forEach(event => {
        Vue.set(
          state.injections,
          event.event_id,
          event
        )
      })
    },
    setPatients (state, payload) {
      Vue.set(state, 'patients', payload)
    },
    setInjections (state, payload) {
      Vue.set(state, 'injections', payload)
    },
    setAppointments (state, payload) {
      Vue.set(state, 'appointments', payload)
    },
    setIsStoreModuleLoaded (state, payload) {
      state.isStoreModuleLoaded = payload
    },
    clearPatientsStoreModule (state) {
      Vue.set(state, 'patients', {})
      Vue.set(state, 'injections', {})
      Vue.set(state, 'appointments', {})
      state.isStoreModuleLoaded = null
    }
  },
  actions: {
    saveOnePatient ({ commit }, payload) {
      return new Promise(resolve => {
        commit('setOnePatient', payload)
        resolve()
      })
    },
    saveOneEvent ({ commit }, payload) {
      return new Promise(resolve => {
        commit('setOneEvent', payload)
        resolve()
      })
    },
    deleteOneEvent ({ commit }, payload) {
      return new Promise(resolve => {
        commit('removeOneEvent', payload)
        resolve()
      })
    },
    removeAndAddManyInjections ({ commit }, { deletedEventIds, createdEvents }) {
      return new Promise(resolve => {
        commit('removeManyInjections', deletedEventIds)
        commit('addManyInjections', createdEvents)
        resolve()
      })
    },
    saveAllPatients ({ commit }, payload) {
      return new Promise(resolve => {
        let sortedPatients = payload
          .map(patient => patient.username)
          .reduce(
            (sortedPatients, username, patientIndex) => {
              sortedPatients[username] = payload[patientIndex]
              return sortedPatients
            },
            {}
          )

        commit('setPatients', sortedPatients)
        resolve()
      })
    },
    saveManyInjections ({ commit }, payload) {
      return new Promise(resolve => {
        commit('addManyInjections', payload)
        resolve()
      })
    },
    saveAllEvents ({ commit, state, getters }, payload) {
      let serializedInjections = (
        payload
          .filter(event => event.category === 'M')
          .reduce(
            (injections, injection) => {
              injections[injection.event_id] = injection

              return injections
            },
            {}
          )
      )

      let serializedAppointments = (
        payload
          .filter(event => event.category === 'V')
          .reduce(
            (appointments, appointment) => {
              appointments[appointment.event_id] = appointment

              return appointments
            },
            {}
          )
      )

      return new Promise(resolve => {
        commit('setInjections', serializedInjections)
        commit('setAppointments', serializedAppointments)
        resolve()
      })
    },
    saveIsStoreModuleLoaded ({ commit }, payload) {
      return new Promise(resolve => {
        commit('setIsStoreModuleLoaded', payload)
        resolve()
      })
    },
    resetPatientsStoreModule ({ commit }) {
      return new Promise(resolve => {
        commit('clearPatientsStoreModule')
        resolve()
      })
    }
  }
}

export default patients



// WEBPACK FOOTER //
// ./src/store/modules/patients.js